import { OvertimeStats } from "../types";

export interface MonthlySummary {
  overtimeChange: number;
  vacationUsed: number;
}

export interface WeeklySummary {
  overtimeTracked: number;
  overtimeGranted: number;
  vacationUsed: number;
}

export const getWeekKey = (date: Date): string => {
  const tempDate = new Date(date.getTime());
  tempDate.setHours(0, 0, 0, 0);
  // Thursday in current week decides the year
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));
  // January 4 is always in week 1
  const week1 = new Date(tempDate.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count number of weeks from date to week1
  const weekNumber =
    1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${tempDate.getFullYear()}-W${weekNumber.toString().padStart(2, "0")}`;
};

export const getMonthKey = (date: Date): string => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
};

export const calculateDailyVacationUsage = (
  currentStat: OvertimeStats,
  nextStat: OvertimeStats | null
): number => {
  return nextStat ? Math.max(0, currentStat.usedVacationMinutes - nextStat.usedVacationMinutes) : 0;
};

export const calculateTotalOvertimeChange = (
  currentStat: OvertimeStats,
  nextStat: OvertimeStats | null
): number => {
  return nextStat ? currentStat.totalOverOrUndertime - nextStat.totalOverOrUndertime : 0;
};

export const calculateSummaries = (
  reversedStats: OvertimeStats[]
): {
  monthlySummaries: Record<string, MonthlySummary>;
  weeklySummaries: Record<string, WeeklySummary>;
} => {
  const monthlySummaries: Record<string, MonthlySummary> = {};
  const weeklySummaries: Record<string, WeeklySummary> = {};

  reversedStats.forEach((stat, index, array) => {
    const statDate = new Date(stat.date);
    const monthKey = getMonthKey(statDate);
    const weekKey = getWeekKey(statDate);

    // Calculate daily vacation usage
    const nextStat = index < array.length - 1 ? array[index + 1] : null;
    const dailyUsedVacationMinutes = calculateDailyVacationUsage(stat, nextStat);

    // Calculate the change in total overtime from previous day
    const totalOvertimeChangeFromPrevious = calculateTotalOvertimeChange(stat, nextStat);

    // Accumulate monthly stats
    if (!monthlySummaries[monthKey]) {
      monthlySummaries[monthKey] = { overtimeChange: 0, vacationUsed: 0 };
    }
    monthlySummaries[monthKey].overtimeChange += stat.overOrUnderTimeMinutes;
    monthlySummaries[monthKey].vacationUsed += dailyUsedVacationMinutes;

    // Accumulate weekly stats
    if (!weeklySummaries[weekKey]) {
      weeklySummaries[weekKey] = { overtimeTracked: 0, overtimeGranted: 0, vacationUsed: 0 };
    }
    weeklySummaries[weekKey].overtimeTracked += stat.overOrUnderTimeMinutes;
    weeklySummaries[weekKey].overtimeGranted += totalOvertimeChangeFromPrevious;
    weeklySummaries[weekKey].vacationUsed += dailyUsedVacationMinutes;
  });

  return { monthlySummaries, weeklySummaries };
};

export const hasOvertimePermission = (
  currentStat: OvertimeStats,
  nextStat: OvertimeStats | null
): boolean => {
  if (!nextStat) return true;
  return currentStat.overOrUnderTimeMinutes <= 0 || currentStat.totalOverOrUndertime > nextStat.totalOverOrUndertime;
};
